import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, ArrowUpRight, Star, Coins } from '@phosphor-icons/react'
import { useBilling, TIER_CONFIG } from '../contexts/BillingContext'

const FEATURES = [
  { key: 'credits', label: 'Créditos / mes', free: '10', pro: '200' },
  { key: 'rollover', label: 'Rollover de créditos', free: false, pro: 'Hasta 60 días' },
  { key: 'analytics', label: 'Analytics de YouTube', free: 'Últimos 28 días', pro: 'Completo' },
  { key: 'retention', label: 'Curva de retención', free: false, pro: true },
  { key: 'traffic', label: 'Fuentes de tráfico', free: false, pro: true },
  { key: 'channels', label: 'Canales conectados', free: '1', pro: '3' },
  { key: 'templates', label: 'Plantilla narrativa', free: false, pro: true },
  { key: 'watermark', label: 'Sin marca de agua', free: false, pro: true },
  { key: 'byok', label: 'API Key propia (ilimitado)', free: false, pro: true },
]

export default function PricingPage() {
  const {
    plan,
    isFreePlan,
    stripeConfig,
    openCheckout,
    openPortal,
    creditPackages,
    creditPricing,
    purchaseCreditPack,
  } = useBilling()
  const [annual, setAnnual] = useState(false)
  const [showWeights, setShowWeights] = useState(false)

  const proMonthlyPrice = annual ? 19 : 29
  const proPriceId = annual ? stripeConfig?.prices?.pro_annual : stripeConfig?.prices?.pro_monthly
  const availablePacks = (creditPackages ?? []).filter(p => p.available)

  const handleProCTA = () => {
    if (!isFreePlan) {
      openPortal()
    } else if (proPriceId) {
      openCheckout(proPriceId)
    } else {
      window.location.href = '/auth'
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="text-center mb-10">
        <h1 className="type-h2 text-ds-text text-3xl mb-3">Elige tu plan</h1>
        <p className="text-ds-muted text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
          Sin compromisos. Cancela cuando quieras.
        </p>

        {/* Monthly / Annual toggle */}
        <div className="flex items-center justify-center gap-3 mt-6">
          <span
            className={`text-sm ${!annual ? 'text-ds-text' : 'text-ds-muted'}`}
            style={{ fontFamily: 'Barlow, sans-serif' }}
          >
            Mensual
          </span>
          <button
            type="button"
            onClick={() => setAnnual(v => !v)}
            aria-pressed={annual}
            aria-label={annual ? 'Cambiar a facturación mensual' : 'Cambiar a facturación anual'}
            className={`relative w-10 h-5 flex-shrink-0 transition-colors ${annual ? 'bg-ds-red' : 'bg-ds-elevated border border-ds-border-mid'}`}
            style={{ borderRadius: 2 }}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 bg-ds-text transition-transform duration-200 ${annual ? 'translate-x-5' : 'translate-x-0'}`}
              style={{ borderRadius: 1 }}
            />
          </button>
          <span
            className={`text-sm ${annual ? 'text-ds-text' : 'text-ds-muted'}`}
            style={{ fontFamily: 'Barlow, sans-serif' }}
          >
            Anual <span className="text-[#30D158] text-xs font-semibold ml-1">-34%</span>
          </span>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid md:grid-cols-2 gap-5 mb-10">
        {/* Free */}
        <div className={`card-elevated p-6 ${plan === 'free' ? 'border-ds-border-mid' : ''}`}>
          <div className="flex items-start justify-between mb-5">
            <div>
              <p className="type-h3 text-ds-text text-base">Free</p>
              <p
                className="text-ds-muted text-xs mt-1"
                style={{ fontFamily: 'Barlow, sans-serif' }}
              >
                Para empezar sin compromiso
              </p>
            </div>
            {plan === 'free' && <span className="badge-dark">Plan actual</span>}
          </div>
          <div className="flex items-baseline gap-1 mb-1">
            <span
              className="text-4xl font-bold text-ds-text"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              $0
            </span>
            <span className="text-ds-muted text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
              /mes
            </span>
          </div>
          <p className="text-ds-muted text-xs mb-5" style={{ fontFamily: 'Barlow, sans-serif' }}>
            10 créditos/mes · sin rollover
          </p>
          <Link to="/auth" className="btn-ghost block w-full text-center py-2.5">
            {plan === 'free' ? 'Plan actual' : 'Empezar gratis'}
          </Link>
        </div>

        {/* Pro */}
        <div className="card p-6 relative overflow-hidden border-t-2 border-t-ds-red">
          <div className="absolute top-0 right-0 bg-ds-red text-white type-caption px-3 py-1">
            <div className="flex items-center gap-1">
              <Star size={10} weight="fill" />
              Popular
            </div>
          </div>
          <div className="flex items-start justify-between mb-5">
            <div>
              <p className="type-h3 text-ds-text text-base">Pro</p>
              <p
                className="text-ds-muted text-xs mt-1"
                style={{ fontFamily: 'Barlow, sans-serif' }}
              >
                Para creadores en serio
              </p>
            </div>
            {plan === 'pro' && <span className="badge-red">Plan actual</span>}
          </div>
          <div className="flex items-baseline gap-1 mb-1">
            <span
              className="text-4xl font-bold text-ds-text"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              ${proMonthlyPrice}
            </span>
            <span className="text-ds-muted text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
              /mes
            </span>
          </div>
          <p className="text-ds-muted text-xs mb-1" style={{ fontFamily: 'Barlow, sans-serif' }}>
            200 créditos/mes · rollover 60 días
          </p>
          {annual && (
            <p className="text-[#30D158] text-xs mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
              ${proMonthlyPrice * 12}/año · Ahorras ${(29 - proMonthlyPrice) * 12}/año
            </p>
          )}
          <button
            onClick={handleProCTA}
            className="btn-accent w-full flex items-center justify-center gap-2 py-2.5 mt-4"
          >
            {!isFreePlan ? 'Gestionar suscripción' : 'Actualizar a Pro'}
            <ArrowUpRight size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* Credit packs */}
      {availablePacks.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Coins size={16} className="text-[#FF9F0A]" weight="fill" />
            <h2 className="type-h3 text-ds-text text-base">Packs de créditos</h2>
            <span className="text-ds-muted text-xs" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Sin suscripción · sin expiración
            </span>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {availablePacks.map(pack => (
              <button
                key={pack.id}
                onClick={() => purchaseCreditPack(pack.id)}
                className="card-elevated hover:border-ds-border-mid p-5 text-left transition-all group"
              >
                <p className="type-caption text-[#FF9F0A] mb-1">{pack.label}</p>
                <p
                  className="text-2xl font-bold text-ds-text mb-0.5"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {pack.credits}
                </p>
                <p
                  className="text-ds-muted text-xs mb-4"
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  créditos
                </p>
                <div className="flex items-center justify-between">
                  <span
                    className="text-ds-text font-semibold"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    ${pack.price_usd}
                  </span>
                  <ArrowUpRight
                    size={14}
                    className="text-ds-muted group-hover:text-[#FF9F0A] transition-colors"
                  />
                </div>
              </button>
            ))}
          </div>
          <p
            className="text-ds-muted text-xs text-center mt-3"
            style={{ fontFamily: 'Barlow, sans-serif' }}
          >
            Los créditos comprados no expiran y se usan antes que los del plan mensual
          </p>
        </div>
      )}

      {/* Credit weight reference — precios servidos por el backend */}
      <div className={`mb-10${creditPricing.length ? '' : ' hidden'}`}>
        <button
          onClick={() => setShowWeights(v => !v)}
          className="flex items-center gap-2 text-ds-muted hover:text-ds-text text-sm transition-colors mb-3"
          style={{ fontFamily: 'Barlow, sans-serif' }}
        >
          <Coins size={14} />
          ¿Cuántos créditos consume cada acción?{' '}
          <span className="text-ds-red text-xs">{showWeights ? 'Ocultar' : 'Ver'}</span>
        </button>
        {showWeights && (
          <div className="card overflow-hidden">
            {creditPricing.map(({ action, label, credits }) => (
              <div
                key={action}
                className="flex items-center justify-between px-5 py-3 border-b border-ds-border last:border-0"
              >
                <span
                  className="text-ds-muted text-sm"
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  {label}
                </span>
                <span
                  className="text-ds-text font-semibold text-sm"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {credits} crédito{credits !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Feature comparison table */}
      <div className="card overflow-hidden">
        <div className="grid grid-cols-3 px-6 py-3 border-b border-ds-border">
          <span className="type-label text-ds-muted">Función</span>
          <span className="type-label text-ds-muted text-center">Free</span>
          <span className="type-label text-ds-red text-center">Pro</span>
        </div>
        {FEATURES.map(({ key, label, free, pro }) => (
          <div
            key={key}
            className="grid grid-cols-3 px-6 py-3.5 border-b border-ds-border last:border-0 items-center"
          >
            <span className="text-ds-text text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
              {label}
            </span>
            <span className="text-center text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
              {typeof free === 'boolean' ? (
                free ? (
                  <Check size={14} className="text-[#30D158] mx-auto" weight="bold" />
                ) : (
                  <span className="text-ds-faint">—</span>
                )
              ) : (
                <span className="text-ds-muted">{free}</span>
              )}
            </span>
            <span className="text-center text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
              {typeof pro === 'boolean' ? (
                pro ? (
                  <Check size={14} className="text-ds-red mx-auto" weight="bold" />
                ) : (
                  <span className="text-ds-faint">—</span>
                )
              ) : (
                <span className="text-ds-text font-medium">{pro}</span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
